import { takeLatest, put, call } from 'redux-saga/effects';
import {
  CampaignReview,
  ICampaign,
  CampaignAnalytics,
  CampaignPagination,
  CampaignContactCounts,
} from '../../types/campaigns/campaigns.types';
import {
  GET_CAMPAIGNS_REQUEST,
  GET_CAMPAIGN_REQUEST,
  CREATE_CAMPAIGN_REQUEST,
  UPDATE_CAMPAIGN_REQUEST,
  PUBLISH_CAMPAIGN_REQUEST,
  GetCampaignsRequestAction,
  GetCampaignRequestAction,
  CreateCampaignRequestAction,
  UpdateCampaignRequestAction,
  PublishCampaignRequestAction,
  ReviewCampaignRequestAction,
  REVIEW_CAMPAIGN_REQUEST,
  GetCampaignAnalyticsRequestAction,
  GET_CAMPAIGN_ANALYTICS_REQUEST,
  DuplicateCampaignRequestAction,
  DUPLICATE_CAMPAIGN_REQUEST,
  DELETE_CAMPAIGN_REQUEST,
  DeleteCampaignRequestAction,
  PAUSE_CAMPAIGN_REQUEST,
  PauseCampaignRequestAction,
  GET_CAMPAIGN_CONTACT_COUNTS_REQUEST,
  GetCampaignContactCountsRequestAction,
} from './campaigns.types';
import {
  getCampaignsSuccess,
  getCampaignsFailure,
  getCampaignSuccess,
  getCampaignFailure,
  createCampaignSuccess,
  createCampaignFailure,
  updateCampaignSuccess,
  updateCampaignFailure,
  publishCampaignSuccess,
  publishCampaignFailure,
  reviewCampaignSuccess,
  reviewCampaignFailure,
  getCampaignAnalyticsSuccess,
  getCampaignAnalyticsFailure,
  duplicateCampaignSuccess,
  duplicateCampaignFailure,
  deleteCampaignSuccess,
  deleteCampaignFailure,
  getCampaignsRequest,
  pauseCampaignSuccess,
  pauseCampaignFailure,
  getCampaignContactCountsSuccess,
  getCampaignContactCountsFailure,
} from './campaigns.action';
import {
  getCampaigns,
  getCampaign,
  createCampaign,
  updateCampaign,
  publishCampaign,
  reviewCampaign,
  getCampaignAnalytics,
  duplicateCampaign,
  deleteCampaign,
  pauseCampaign,
  getCampaignContactCounts,
} from '../../services/campaignsService/campaignsService';

// Saga workers
function* getCampaignsWorker({ payload: filters }: GetCampaignsRequestAction) {
  try {
    const response: { campaigns: ICampaign[]; pagination: CampaignPagination } =
      yield call(getCampaigns, filters);
    yield put(getCampaignsSuccess(response.campaigns, response.pagination));
  } catch (error: any) {
    yield put(
      getCampaignsFailure(error.response?.data || 'Failed to fetch campaigns')
    );
  }
}

function* getCampaignWorker({ payload: campaignId }: GetCampaignRequestAction) {
  try {
    const campaign: ICampaign = yield call(getCampaign, campaignId);
    yield put(getCampaignSuccess(campaign));
  } catch (error: any) {
    yield put(
      getCampaignFailure(error.response?.data || 'Failed to fetch campaign')
    );
  }
}

function* createCampaignWorker({
  payload: campaignData,
}: CreateCampaignRequestAction) {
  try {
    const campaign: ICampaign = yield call(createCampaign, campaignData);
    yield put(createCampaignSuccess(campaign));
  } catch (error: any) {
    yield put(
      createCampaignFailure(
        error.response?.data?.message ||
          error.message ||
          'Failed to create campaign'
      )
    );
  }
}

function* updateCampaignWorker({
  payload: { campaignId, campaignData },
}: UpdateCampaignRequestAction) {
  try {
    const campaign: ICampaign = yield call(
      updateCampaign,
      campaignId,
      campaignData
    );
    yield put(updateCampaignSuccess(campaign));
  } catch (error: any) {
    yield put(
      updateCampaignFailure(error.response?.data || 'Failed to update campaign')
    );
  }
}

function* publishCampaignWorker({
  payload: { campaignId, selectedType },
}: PublishCampaignRequestAction) {
  try {
    const campaign: ICampaign = yield call(
      publishCampaign,
      campaignId,
      selectedType
    );
    yield put(publishCampaignSuccess(campaign));
  } catch (error: any) {
    yield put(
      publishCampaignFailure(
        error.response?.data || 'Failed to publish campaign'
      )
    );
  }
}

function* reviewCampaignWorker({
  payload: { campaignId },
}: ReviewCampaignRequestAction) {
  try {
    const campaign: CampaignReview = yield call(reviewCampaign, campaignId);
    yield put(reviewCampaignSuccess(campaign));
  } catch (error: any) {
    yield put(
      reviewCampaignFailure(error.response?.data || 'Failed to review campaign')
    );
  }
}

function* getCampaignAnalyticsWorker({
  payload: { campaignId, startDate, endDate },
}: GetCampaignAnalyticsRequestAction) {
  try {
    const analytics: CampaignAnalytics = yield call(
      getCampaignAnalytics,
      campaignId,
      startDate,
      endDate
    );
    yield put(getCampaignAnalyticsSuccess(analytics));
  } catch (error: any) {
    yield put(
      getCampaignAnalyticsFailure(
        error.response?.data || 'Failed to fetch campaign analytics'
      )
    );
  }
}

function* duplicateCampaignWorker({
  payload: campaignId,
}: DuplicateCampaignRequestAction) {
  try {
    const campaign: ICampaign = yield call(duplicateCampaign, campaignId);
    yield put(duplicateCampaignSuccess(campaign));
  } catch (error: any) {
    yield put(
      duplicateCampaignFailure(
        error.response?.data || 'Failed to duplicate campaign'
      )
    );
  }
}

function* deleteCampaignWorker({
  payload: { campaignId, filters },
}: DeleteCampaignRequestAction) {
  try {
    yield call(deleteCampaign, campaignId);
    yield put(deleteCampaignSuccess(campaignId));
    // Refresh campaigns list using provided filters (if any)
    yield put(getCampaignsRequest(filters));
  } catch (error: any) {
    yield put(
      deleteCampaignFailure(error.response?.data || 'Failed to delete campaign')
    );
  }
}

function* pauseCampaignWorker({
  payload: campaignId,
}: PauseCampaignRequestAction) {
  try {
    const campaign: ICampaign = yield call(pauseCampaign, campaignId);
    yield put(pauseCampaignSuccess(campaign));
  } catch (error: any) {
    yield put(
      pauseCampaignFailure(error.response?.data || 'Failed to pause campaign')
    );
  }
}

function* getCampaignContactCountsWorker({
  payload: campaignId,
}: GetCampaignContactCountsRequestAction) {
  try {
    const contactCounts: CampaignContactCounts = yield call(
      getCampaignContactCounts,
      campaignId
    );
    yield put(getCampaignContactCountsSuccess(contactCounts));
  } catch (error: any) {
    yield put(
      getCampaignContactCountsFailure(
        error.response?.data?.message ||
          error.message ||
          'Failed to fetch campaign contact counts'
      )
    );
  }
}

// Saga watchers
export function* campaignSaga() {
  yield takeLatest(GET_CAMPAIGNS_REQUEST, getCampaignsWorker);
  yield takeLatest(GET_CAMPAIGN_REQUEST, getCampaignWorker);
  yield takeLatest(CREATE_CAMPAIGN_REQUEST, createCampaignWorker);
  yield takeLatest(UPDATE_CAMPAIGN_REQUEST, updateCampaignWorker);
  yield takeLatest(PUBLISH_CAMPAIGN_REQUEST, publishCampaignWorker);
  yield takeLatest(REVIEW_CAMPAIGN_REQUEST, reviewCampaignWorker);
  yield takeLatest(GET_CAMPAIGN_ANALYTICS_REQUEST, getCampaignAnalyticsWorker);
  yield takeLatest(
    GET_CAMPAIGN_CONTACT_COUNTS_REQUEST,
    getCampaignContactCountsWorker
  );
  yield takeLatest(DUPLICATE_CAMPAIGN_REQUEST, duplicateCampaignWorker);
  yield takeLatest(DELETE_CAMPAIGN_REQUEST, deleteCampaignWorker);
  yield takeLatest(PAUSE_CAMPAIGN_REQUEST, pauseCampaignWorker);
}
